import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A material's `next_pass` chain: one extra surface draw per resolved link, in chain
 * order after its base, as `_geometry_instance_add_surface_with_material_chain` adds
 * the surface once for the start material and once per link of its
 * `while (material->next_pass.is_valid())` walk
 * (`render_forward_clustered.cpp:4183-4201`), starting from the effective surface
 * material and appending a valid overlay's chain after the whole surface chain
 * (`:4202-4241`). A link that reads the stencil draws nothing, because this canvas
 * requests no stencil buffer, and the walk continues past it; Godot refuses cycles at
 * the setter (`material.cpp:39-42`), so only a hand-written file can produce one, and
 * the visited set draws each material once.
 */
import { useCallback, useEffect, useMemo, useLayoutEffect, useRef, useState, } from 'react';
import * as THREE from 'three';
import { materialSourceKey, resolveNextPass } from './materialSource';
import { NO_RAYCAST } from './materialOverlay';
import { drawnTag } from '../meshes/meshContent';
import { readyMaterial, useMaterial } from './useMaterial';
import { SurfaceMaterialSlot } from './SurfaceMaterialSlot';
import { surfaceAttach, surfaceSources } from './SurfaceMaterialSlots';
import { useOneSurfaceStartingMaterial } from './useOneSurfaceStartingMaterial';
import { visualLayersUserData } from '../visualLayers';
import { parseStandardMaterial3DScalars } from '../../resources/materials/standardmaterial3d/scalars';
/**
 * The visited seed every chain's walk starts from: nothing has drawn yet, so no key is
 * held. The walkers never mutate their visited set — `visitedWith` returns a new one —
 * so one instance serves every group, every owner and every overlay link per render.
 *
 * @canonical The visited seed of a next_pass chain walk
 */
export const NOTHING_DRAWN = new Set();
/**
 * The draw order of a next_pass chain draw: the surface's own draw is ordinal 0, each
 * resolved link takes its index, a stencil-skipped link leaves a numbered gap, and the
 * overlay mounts one past the greatest ordinal any chain reached.
 *
 * @canonical The draw order of a next_pass chain draw
 */
export function nextPassRenderOrder(ordinal) {
    return ordinal;
}
/**
 * The element and bind props of a draw the base draws: `THREE.Points` for a points
 * surface, and a detached `THREE.SkinnedMesh` for a skinned one, as the skin matrices
 * stay in mesh space and the node's transform applies after them.
 *
 * @canonical The drawn element of a surface draw
 */
export function drawnElementOf(drawsPoints, skeleton) {
    if (skeleton && !drawsPoints) {
        return { Drawn: 'skinnedMesh', skin: { skeleton, bindMode: THREE.DetachedBindMode } };
    }
    return { Drawn: drawnTag(drawsPoints), skin: {} };
}
/**
 * Reads the geometry off the base draw, in place across re-parses. Dep-less, since a
 * re-parse replaces the geometry and a `[ref]` list would keep the old one; the updater
 * returns the same value when nothing moved, so the pair cannot loop. The layout pass
 * swaps a re-parsed geometry before paint, and on mount it runs before the parent's
 * ref lands, so the passive pass reads the first geometry.
 *
 * @canonical The geometry a surface draw reads back
 */
export function useDrawnGeometry(ref) {
    const [geometry, setGeometry] = useState(null);
    const readBack = () => {
        const attached = ref.current?.geometry ?? null;
        setGeometry((previous) => (previous === attached ? previous : attached));
    };
    useLayoutEffect(readBack);
    useEffect(readBack);
    return geometry;
}
/** One extra draw of the base geometry, as the overlay's mirrors it: unpicked, receiving only. */
function ChainDraw({ geometry, source, ordinal, attach, surfaceCount, drawsPoints, skeleton, godotLayers, shadow, }) {
    const startingMaterial = useOneSurfaceStartingMaterial(surfaceCount);
    const { Drawn, skin } = drawnElementOf(drawsPoints, skeleton);
    return (_jsx(Drawn, { ...skin, args: [geometry, startingMaterial], geometry: geometry, renderOrder: nextPassRenderOrder(ordinal), 
        // The base draw is the node's pickable body; a coincident second hit would
        // report the same node twice.
        raycast: NO_RAYCAST, receiveShadow: true, onBeforeRender: shadow.onBeforeRender, onAfterRender: shadow.onAfterRender, 
        // The node's layers mask, which a decal's and a reflection probe's masks filter on.
        userData: visualLayersUserData(godotLayers), children: _jsx(SurfaceMaterialSlot, { source: source, attach: attach, points: drawsPoints ? 'mesh' : undefined }) }));
}
/**
 * One pass of a chain, recursive. The starting pass draws nothing of its own; every
 * deeper pass draws itself and resolves its own continuation, so the arrival mounts
 * the link when its file lands. A stencil-reading pass draws nothing and the walk
 * continues past it; a pass whose material already drew is cut, drawing each material
 * of a hand-written cycle once.
 *
 * @canonical One pass of a material's next_pass chain
 */
export function NextPassLink(props) {
    const { source, ordinal, drawsSelf, visited, geometry, attach, surfaceCount, onOrdinal, draw, shadow } = props;
    const loaded = useMaterial(source);
    const material = readyMaterial(loaded);
    const scalars = useMemo(() => (material ? parseStandardMaterial3DScalars(material.resource.data) : null), [material]);
    // Stable between loads: a re-render of an already-loaded link re-scans nothing.
    const nextSource = useMemo(() => (material && scalars ? resolveNextPass(scalars.nextPass, material) : undefined), [material, scalars]);
    const nextVisited = useMemo(() => visitedWith(visited, source), [visited, source]);
    // The pass's fate reported once it is known, in the layout pass: a resolved one
    // reached its position whether it drew or was skipped over, and a declined one ends
    // its chain there. A pass still loading waits, and the overlay's own order then
    // moves past it in the same frame the link draws, before paint.
    useLayoutEffect(() => {
        if (material || loaded.status === 'declined')
            onOrdinal?.(ordinal);
    }, [loaded.status, material, ordinal, onOrdinal]);
    // A pass whose material already drew on this surface is cut: the walk drew it once
    // and continued through it already, so its repeat ends here.
    if (source && visited.has(materialSourceKey(source)))
        return null;
    if (!material || !scalars || !source)
        return null;
    const continuation = nextSource !== undefined ? (_jsx(NextPassLink, { source: nextSource, ordinal: ordinal + 1, drawsSelf: true, visited: nextVisited, geometry: geometry, attach: attach, surfaceCount: surfaceCount, draw: draw, onOrdinal: onOrdinal, drawsPoints: props.drawsPoints, skeleton: props.skeleton, godotLayers: props.godotLayers, shadow: shadow })) : null;
    // No stencil buffer is requested, so a stencil-reading link draws nothing and the
    // walk continues past it at the next ordinal, the starting pass's draw kept.
    if (scalars.readsStencil)
        return continuation;
    if (!geometry)
        return null;
    const site = {
        source,
        ordinal,
        attach,
        surfaceCount,
        geometry,
        drawsPoints: props.drawsPoints,
        skeleton: props.skeleton,
        godotLayers: props.godotLayers,
        shadow,
    };
    return (_jsxs(_Fragment, { children: [drawsSelf && (draw ? draw(site) : _jsx(ChainDraw, { ...site })), continuation] }));
}
/**
 * The visited set the next pass walks with: every key so far, plus this pass's own. The
 * imperative GLB walk shares it, so a hand-written cycle draws each material once on
 * both.
 *
 * @canonical The visited set of a next_pass chain walk
 */
export function visitedWith(visited, source) {
    if (!source)
        return visited;
    const own = materialSourceKey(source);
    if (visited.has(own))
        return visited;
    return new Set([...visited, own]);
}
/**
 * One part's next_pass chain across all of its draw groups, plus the part's overlay
 * mount through its own factory, whose draw lands one past the greatest ordinal any
 * chain reached — `MATERIAL_OVERLAY_RENDER_ORDER` for a part with no chain. Mounts
 * nothing extra for a part whose every group resolves to no chain.
 */
export function NextPassChain({ meshRef, geometry: ownGeometry, sources, overlay, draw, drawsPoints, skeleton, godotLayers, shadow, }) {
    // Owners that hold their geometry directly leave the read-back nothing to read: a
    // stable ref keeps the hook whole without one.
    const readBackRef = useRef(null);
    const readBack = useDrawnGeometry(meshRef ?? readBackRef);
    const geometry = ownGeometry !== undefined ? ownGeometry : readBack;
    const [reached, setReached] = useState(0);
    const onOrdinal = useCallback((ordinal) => setReached((greatest) => Math.max(greatest, ordinal)), []);
    const groups = surfaceSources(sources);
    return (_jsxs(_Fragment, { children: [groups.map((source, group) => (_jsx(NextPassLink, { source: source, ordinal: 0, drawsSelf: false, 
                // The starting pass's own key lands in the set when it recurses; the set
                // holds only what has drawn, so the pass's fate is its own to decide.
                visited: NOTHING_DRAWN, geometry: geometry, attach: surfaceAttach(group, groups.length), surfaceCount: groups.length, draw: draw, onOrdinal: onOrdinal, drawsPoints: drawsPoints, skeleton: skeleton, godotLayers: godotLayers, shadow: shadow }, group))), geometry && overlay && overlay(nextPassRenderOrder(reached + 1))] }));
}
//# sourceMappingURL=nextPass.js.map